import React, { useEffect, useState } from 'react';
import { Option } from 'react-select/src/filters';
import { spacing } from '../../tokens/sizing';
import { FlexContainer, Container, FlexContainerProps } from '..';
import { NakedSelect, SelectProps } from '../Select';
import InputWrapper, { extractWrapperProps } from '../utils/InputWrapper';

let idCounter = 0;

const DateTimeContainer = (props: FlexContainerProps) => (
    <FlexContainer
        sx={{
            '> *': {
                flex: 'none',
                flexGrow: 1,
                marginRight: spacing.small,
                ':last-child': {
                    marginRight: spacing.none,
                },
            },
        }}
        {...props}
    />
);

function addLeadingZero(value: string | number) {
    return ('0' + value).slice(-2);
}
const monthInfo = [
    {
        name: 'Jan',
        getDays: () => 31,
    },
    {
        name: 'Feb',
        getDays: (year: Option) => {
            function isLeapYear(year: number) {
                return (year % 4 == 0 && year % 100 != 0) || year % 400 == 0;
            }
            return year ? (isLeapYear(parseInt(year.value)) ? 29 : 28) : 29;
        },
    },
    {
        name: 'Mar',
        getDays: () => 31,
    },
    {
        name: 'Apr',
        getDays: () => 30,
    },
    {
        name: 'May',
        getDays: () => 31,
    },
    {
        name: 'Jun',
        getDays: () => 30,
    },
    {
        name: 'Jul',
        getDays: () => 31,
    },
    {
        name: 'Aug',
        getDays: () => 31,
    },
    {
        name: 'Sep',
        getDays: () => 30,
    },
    {
        name: 'Oct',
        getDays: () => 31,
    },
    {
        name: 'Nov',
        getDays: () => 30,
    },
    {
        name: 'Dec',
        getDays: () => 31,
    },
];
function getMonth(monthNum: number) {
    return monthInfo[monthNum - 1];
}
function mergeFilters(...filters: Array<(input: string | number) => string>) {
    return (inputValue: string) => {
        return filters.reduce((value, filter) => filter(value), inputValue);
    };
}
function limitLength(limit: number) {
    return (inputValue: string) => (inputValue.length <= limit ? inputValue : inputValue.substr(0, limit));
}
function setType(type: 'number' | 'text') {
    return (inputValue: string) => {
        switch (type) {
            case 'number':
                return inputValue.replace(/\D/g, '');
            case 'text':
                return inputValue.replace(/[0-9]/g, '');
            default:
                return inputValue;
        }
    };
}

function useSetDefaultValue({ defaultValue, setYear, setMonth, setDay }: any) {
    useEffect(() => {
        if (defaultValue) {
            const [defaultYear = '', defaultMonth = '', defaultDay = ''] = defaultValue.split('-');
            if (defaultYear.length === 4 && defaultMonth.length === 2 && defaultDay.length === 2) {
                const monthNumber = parseInt(defaultMonth);
                const dayNumber = parseInt(defaultDay);
                const yearNumber = parseInt(defaultYear);

                const foundMonth = getMonth(monthNumber);
                const yearSelection = { label: yearNumber, value: yearNumber };
                let maxDaysInMonth = 31;
                setYear(yearSelection);
                if (foundMonth) {
                    setMonth({ label: foundMonth.name, value: monthNumber });
                    maxDaysInMonth = foundMonth.getDays(yearSelection as any);
                }
                if (dayNumber <= maxDaysInMonth && dayNumber > 0) {
                    setDay({ label: addLeadingZero(dayNumber), value: dayNumber });
                }
            }
        }
    }, [defaultValue]);
}

function calculateInputValue({ month, day, year }: { month: Option | null; day: Option | null; year: Option | null }) {
    if (!day || !month || !year) {
        return '';
    } else {
        const yearValue = year.value;
        const dayValue = addLeadingZero(day.value);
        const monthValue = addLeadingZero(month.value);
        return `${yearValue}-${monthValue}-${dayValue}`;
    }
}

export interface DateInputProps extends Omit<SelectProps, 'onChange' | 'options' | 'value' | 'defaultValue'> {
    onChange?: (inputValue: string) => void;
    numberYearsSupported?: number;
    startingYear?: number;
    value?: string /* Currently not wired up to control this component */;

    /**
     * Format should be 'YYYY-MM-DD'
     */
    defaultValue?: string;
}

const DateInput = React.forwardRef((props: DateInputProps, ref: any): JSX.Element => {
    const sharedProps = {
        isError: props.isError,
        disabled: props.disabled,
    };

    // Used to tie our hidden input to our label, which if we didn't would cause some funky
    // input wrapper errors.
    const autoId = React.useMemo(() => `date-input-${idCounter++}`, []);
    const [month, setMonth] = useState(null);
    const [day, setDay] = useState(null);
    const [year, setYear] = useState(null);
    const [inputValue, setInputValue] = useState('');

    useSetDefaultValue({ defaultValue: props.defaultValue, setMonth, setYear, setDay });
    useEffect(() => {
        const inputValue = calculateInputValue({ month, day, year });
        setInputValue(inputValue);
        props.onChange(inputValue);
    }, [month, day, year]);

    function onChangeDay(newValue: Option) {
        setDay(newValue);
    }

    function onChangeMonth(newValue: Option) {
        if (day && day.value > getMonth(parseInt(newValue.value)).getDays(year)) {
            setDay(null);
        }
        setMonth(newValue);
    }

    function onChangeYear(newValue: Option) {
        if (day && month && day.value > getMonth(month.value).getDays(newValue)) {
            setDay(null);
        }
        setYear(newValue);
    }

    const { extractedProps: wrapperProps } = extractWrapperProps<DateInputProps>(props);

    const monthOptions = React.useMemo(
        () =>
            monthInfo.map((monthData, index) => ({
                label: `${monthData.name} - ${addLeadingZero(index + 1)}`,
                value: index + 1,
                name: monthData.name,
            })),
        []
    );

    const dayOptions = React.useMemo(
        () =>
            Array.from({ length: month ? getMonth(month.value).getDays(year) : 31 }).map((_, index) => ({
                label: addLeadingZero(index + 1),
                value: index + 1,
            })),
        [month, year]
    );

    const yearOptions = React.useMemo(
        () =>
            Array.from({ length: props.numberYearsSupported }).map((_, index) => ({
                label: props.startingYear - index,
                value: props.startingYear - index,
            })),
        [props.numberYearsSupported, props.startingYear]
    );

    return (
        <InputWrapper htmlFor={props?.id || autoId} {...wrapperProps}>
            <DateTimeContainer padding="none" justifyContent="space-between">
                <Container width={1 / 3}>
                    <NakedSelect
                        name="month"
                        value={month}
                        onChange={onChangeMonth}
                        onInputChange={mergeFilters(limitLength(5))}
                        placeholder="Mo."
                        options={monthOptions}
                        {...sharedProps}
                    />
                </Container>
                <Container width={1 / 3}>
                    <NakedSelect
                        name="day"
                        value={day}
                        onChange={onChangeDay}
                        placeholder="Day"
                        onInputChange={mergeFilters(limitLength(2), setType('number'))}
                        options={dayOptions}
                        {...sharedProps}
                    />
                </Container>
                <Container width={1 / 3}>
                    <NakedSelect
                        name="year"
                        value={year}
                        onChange={onChangeYear}
                        placeholder="Year"
                        onInputChange={mergeFilters(limitLength(4), setType('number'))}
                        options={yearOptions}
                        {...sharedProps}
                    />
                </Container>
            </DateTimeContainer>
            <input id={props.id || autoId} name={props.name} type="hidden" value={inputValue} ref={ref} />
        </InputWrapper>
    );
});
DateInput.displayName = 'DateInput';
DateInput.defaultProps = {
    onChange: () => {
        /* do nothing */
    },
    startingYear: new Date().getFullYear(),
    numberYearsSupported: 100,
};
export default DateInput;
